import { Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	Divider,
	formatSniCode,
	formatSsn,
	Grid,
	Heading,
	Li,
	Markdown,
	moneyFormatNoDecimal,
	P,
	Stack,
	Text,
	Ul,
	useSigning,
	useText,
} from "@sveabank/cc-common";
import { useState } from "react";
import { useDispatch } from "react-redux";
import Card from "../../../../components/card";
import { usePageContext } from "../../../../hooks/context";
import { useApplicationId } from "../../../../hooks/useApplicationId";
import {
	useCancelClcSignMutation,
	useGetClcSigningStatusMutation,
	useInitializeClcAgreementSigningMutation,
	useLazyFetchClcAgreementQuery,
	usePostClcApplicationMutation,
} from "../../../../services/clc-application";
import { setError } from "../../../../slices/errorSlice";
import { normalizeError } from "../../../../utils/error-handling";
import AgreementSigningModal from "../../../sign-page/agreement-signing";
import { useClcCompanyApplication } from "../../hooks";
import {
	formatNumericValue,
	getIsPersonShareholder,
	getTotalAddonCost,
	getTotalDistributedShares,
} from "../../utils";
import ClcCardSummary from "./card-summary";
import ClcSuccessModal from "./success-modal";

type Props = {
	onCancel: () => void;
};

const ClcAgreement = ({ onCancel }: Props) => {
	const dispatch = useDispatch();
	const applicationId = useApplicationId();
	const [t, texts] = useText();
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const {
		clcApplicationDetails: {
			applicant,
			beneficialOwners,
			companyRegistration: { value: companyRegistration },
			companyShares,
			representatives,
			packageAgreement,
		},
		selectedPackage,
		cards: {
			debitCards: { cards },
			isComplete: isCardsComplete,
		},
		controlTypeMap,
		roles,
		setupClcAgreement,
		allSniCodes,
		signatoryCombinations,
		isPostingClcAgreement,
	} = usePageContext(useClcCompanyApplication);

	const [submitApplication] = usePostClcApplicationMutation();

	const [initAgreementSigningRequest] = useInitializeClcAgreementSigningMutation();
	const [getSigningStatus] = useGetClcSigningStatusMutation();
	const [cancelSign] = useCancelClcSignMutation();

	const [fetchClcAgreement, { data: clcAgreement, isFetching: isFetchingClcAgreement }] =
		useLazyFetchClcAgreementQuery();
	const { requestWithProductSigning } = useSigning();

	const [isOpen, setIsOpen] = useState(false);

	const handleOpenSignModal = async () => {
		await setupClcAgreement(applicationId).unwrap();
		await fetchClcAgreement(applicationId).unwrap();
		setIsOpen(true);
	};

	if (!isCardsComplete) return null;

	const signatoryCombinationDescription =
		representatives.value?.signatoryCombination.signatoryCombination === "Other"
			? representatives.value.signatoryCombination.details
			: signatoryCombinations
					.find((s) => s.value === representatives.value?.signatoryCombination.signatoryCombination)
					?.labels.find((label) => label.language === "sv")?.label || "";

	const packages = texts.newCompany.packages;
	const text = packages[selectedPackage?.packageId];

	const handleSigning = async () => {
		const init = () => initAgreementSigningRequest({ applicationId }).unwrap();
		const status = () => getSigningStatus({ applicationId }).unwrap();
		const cancel = () => cancelSign(applicationId).unwrap();

		try {
			const request = requestWithProductSigning(init, status, cancel);

			await request(applicationId);

			submitApplication(applicationId);
			setIsOpen(false);

			setIsSuccessModalOpen(true);
		} catch (error) {
			if (error instanceof Error) {
				if (error.message === "BankIdTimeout") {
					dispatch(setError(normalizeError(error)));
					return;
				}
			}
			throw error;
		}
	};

	return (
		<Grid>
			<Grid.Col
				span={{
					base: 12,
					tablet: 8,
				}}
			>
				<Stack gap={8}>
					<Stack gap={4}>
						<Heading.H4>{t(texts.newCompany.companyInfo.heading)}</Heading.H4>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyInfo.addressHeading)}
								</Text>
								{!!companyRegistration?.companyAddress?.coAddress && (
									<Text>
										{`${t(texts.newCompany.companyInfo.coAddress)}: ${companyRegistration?.companyAddress?.coAddress}`}
									</Text>
								)}
								<Text>
									{`${t(texts.newCompany.companyInfo.address)}: ${companyRegistration?.companyAddress?.addressLine}`}
								</Text>
								<Text>
									{`${t(texts.newCompany.companyInfo.zip)}: ${companyRegistration?.companyAddress?.zipCode}`}
								</Text>
								<Text>
									{`${t(texts.newCompany.companyInfo.city)}: ${companyRegistration?.companyAddress?.postalCity}`}
								</Text>
							</Stack>
						</Card>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyInfo.contactHeading)}
								</Text>
								<Text>{`${applicant.firstName} ${applicant.lastName}`}</Text>
								<Text>{`${applicant.email}`}</Text>
								<Text>{`${applicant.phone?.countryCode} ${applicant.phone?.number}`}</Text>
							</Stack>
						</Card>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyInfo.namesHeading)}
								</Text>
								{companyRegistration?.proposedCompanyNames.map((name, index) => (
									<Text key={name}>{`${index + 1}. ${name}`}</Text>
								))}
							</Stack>
						</Card>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyInfo.businessDescription)}
								</Text>
								<Text>{companyRegistration?.businessDescription}</Text>
							</Stack>
						</Card>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyInfo.sni)}
								</Text>
								<Stack>
									{companyRegistration?.sniCodes.map((code) => {
										const { description = "", sniCode = "" } =
											allSniCodes.find((c) => c.sniCode === code) || {};
										return <Text key={code}>{`${formatSniCode(sniCode)} ${description}`}</Text>;
									})}
								</Stack>
							</Stack>
						</Card>
					</Stack>
					<Stack gap={4}>
						<Heading.H4>{t(texts.newCompany.shares.title)}</Heading.H4>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack>
								<Text>
									{`${t(texts.newCompany.shares.capital)}: ${formatNumericValue(companyShares.value?.shareCapital)}`}
								</Text>
								<Text>
									{`${t(texts.newCompany.shares.numberOfShares)}: ${formatNumericValue(getTotalDistributedShares(companyShares.value?.shareholders))}`}
								</Text>
								<Text>
									{`${t(texts.newCompany.shares.sharePrice)}: ${formatNumericValue((companyShares.value?.shareCapital || 0) / (getTotalDistributedShares(companyShares.value?.shareholders) || 1))}`}
								</Text>
							</Stack>
						</Card>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack gap={4}>
								<Text bold className="text-lg">
									{t(texts.newCompany.shares.shareholdersHeading)}
								</Text>
								{companyShares.value?.shareholders.map((s) => (
									<Stack key={s.nationalId}>
										<P>
											<Text bold>{`${s.firstName} ${s.lastName}`}</Text>
										</P>
										<Text>{formatSsn(s.nationalId)}</Text>
										<Text>{`${t(texts.newCompany.shares.sharesNumber, formatNumericValue(s.numberOfShares))}`}</Text>
									</Stack>
								))}
							</Stack>
						</Card>
					</Stack>
					<Stack gap={4}>
						<Heading.H4>{t(texts.newCompany.beneficialOwnership.title)}</Heading.H4>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack gap={4}>
								{beneficialOwners.value?.beneficialOwners.map((b) => (
									<Stack gap={4} key={b.nationalId}>
										<Stack>
											<P>
												<Text bold>{`${b.firstName} ${b.lastName}`}</Text>
											</P>
											<Text>{formatSsn(b.nationalId)}</Text>
											{getIsPersonShareholder(b.nationalId, companyShares.value?.shareholders) && (
												<Text>{t(texts.newCompany.shareholder)}</Text>
											)}
										</Stack>
										<Stack>
											<Text bold>{t(texts.newCompany.beneficialOwnership.controlHeading)}</Text>
											<Ul>
												{b.controlTypes.map((c) => {
													const description = controlTypeMap.find((cm) => cm.value === c)?.label;
													return description ? <Li key={c}>{description}</Li> : null;
												})}
											</Ul>
										</Stack>
										<Stack>
											<Text bold>
												{t(texts.newCompany.beneficialOwnership.controlRangeHeading)}
											</Text>
											<Text>
												{b.ownershipPercentMin === 100
													? "100%"
													: t(
															texts.newCompany.beneficialOwnership.controlRangeDescription,
															b.ownershipPercentMin.toString(),
															b.ownershipPercentMax.toString(),
														)}
											</Text>
										</Stack>
									</Stack>
								))}
							</Stack>
						</Card>
					</Stack>
					<Stack gap={4}>
						<Heading.H4>{t(texts.newCompany.companyStructure.title)}</Heading.H4>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack gap={4}>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyStructure.representativesTitle)}
								</Text>
								{representatives.value?.representatives.map((r) => (
									<Stack key={r.nationalId}>
										<P>
											<Text bold>{`${r.firstName} ${r.lastName}`}</Text>
										</P>
										<Text>{formatSsn(r.nationalId)}</Text>
										{getIsPersonShareholder(r.nationalId, companyShares.value?.shareholders) && (
											<Text>{t(texts.newCompany.shareholder)}</Text>
										)}
										{r.functions?.map((f) => {
											const description = roles.find((r) => r.value === f)?.label;
											return description ? <Text key={f}>{description}</Text> : null;
										})}
									</Stack>
								))}
							</Stack>
						</Card>
					</Stack>
					<Stack gap={4}>
						<Heading.H4>
							{t(texts.newCompany.companyStructure.signatoryCombinationTitle)}
						</Heading.H4>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Text>{signatoryCombinationDescription}</Text>
						</Card>
					</Stack>
					<Stack gap={4}>
						<Heading.H4>{t(texts.newCompany.agreement.packageAndCardsHeading)}</Heading.H4>
						<Card classNames="bg-background-tertiary" fullWidth>
							<Stack gap={4}>
								<Text bold className="text-lg">
									{t(text.summary.title)}
								</Text>
								<Markdown>{t(text.summary.packageProductBullets)}</Markdown>
								<Divider style="dashed" className="border-foreground-info" height={1} />
								<ClcCardSummary cardHolderNationalId={applicant.nationalId} />
								<Divider style="dashed" className="border-foreground-info" height={1} />
								<Text bold align="end">
									{t(
										text.summary.packageMonthlyCost,
										moneyFormatNoDecimal(Number(selectedPackage.monthlyFee.amount)),
									)}
								</Text>
							</Stack>
						</Card>
						{selectedPackage.products.filter((p) => p.isAddon).length > 0 && (
							<Card classNames="bg-background-tertiary" fullWidth>
								<Stack gap={2}>
									<Text className="text-lg" bold>
										{t(text.summary.extraDebitCards)}
									</Text>
									<Divider style="dashed" className="border-foreground-info" height={1} />
									{selectedPackage.products
										.filter((p) => p.isAddon)
										.map((p) => cards.find((c) => c.productId === p.productId))
										.filter((c) => !!c && c.isConfigured)
										.map((c) => (
											<Stack gap={4} key={c.productId}>
												<ClcCardSummary cardHolderNationalId={c.nationalId} />
												<Divider style="dashed" className="border-foreground-info" height={1} />
											</Stack>
										))}
									<Text align="end" bold>
										{t(
											text.summary.extraDebitCardsTotalCost,
											getTotalAddonCost(selectedPackage).toString(),
										)}
									</Text>
								</Stack>
							</Card>
						)}
					</Stack>
					<ButtonGroup row align="start">
						<Button type="button" variant="secondary" onPress={onCancel}>
							{t(texts.common.abort)}
						</Button>
						<Button
							isDisabled={packageAgreement.isComplete}
							iconStart="bankId"
							isPending={isFetchingClcAgreement || isPostingClcAgreement}
							onPress={handleOpenSignModal}
							data-tracking-action="CLC Application Open Sign Modal"
						>
							{t(texts.newCompany.agreement.sign)}
						</Button>
					</ButtonGroup>
				</Stack>

				{clcAgreement && (
					<AgreementSigningModal
						isModalOpen={isOpen}
						setOpen={setIsOpen}
						title={t(texts.newCompany.agreement.agreementHeading)}
						signAction={handleSigning}
						isSigning={false}
						pdfData={clcAgreement}
					/>
				)}

				<ClcSuccessModal
					isOpen={isSuccessModalOpen}
					closeModal={() => setIsSuccessModalOpen(false)}
				/>
			</Grid.Col>
		</Grid>
	);
};

export default ClcAgreement;
